import React, { useState, useEffect } from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
  Badge,
  VStack,
  Divider,
  Container,
  Icon,
  CloseButton,
} from "@chakra-ui/react";
import { ChevronRightIcon, SearchIcon } from "@chakra-ui/icons";
import { FiPackage, FiFileText, FiClock, FiCheck, FiShoppingBag, FiTruck } from "react-icons/fi";
import { Link } from "react-router-dom";
import { apiUrl } from "../ApiUrl/ApiUrl";

function OrderPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const uid = localStorage.getItem("uid");

  const fetchOrders = () => {
    if (!uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${apiUrl}/public/getOrdersByUid?uid=${uid}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch order history");
        }
        return res.json();
      })
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching orders:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter((order) => {
    const term = searchTerm.toLowerCase();
    const matchesOrderId =
      order.orderId && order.orderId.toLowerCase().includes(term);
    const matchesItem =
      order.items &&
      order.items.some(
        (item) => item.booktitle && item.booktitle.toLowerCase().includes(term)
      );
    return matchesOrderId || matchesItem;
  });

  return (
    <Box minH="85vh" bg="#F8FAFC" py={{ base: 4, md: 8 }}>
      <Container maxW="1200px" px={{ base: 3, md: 6 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          spacing="8px"
          separator={<ChevronRightIcon color="gray.400" />}
          fontSize="xs"
          color="gray.500"
          mb={4}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/" _hover={{ color: "#162237" }}>
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/myprofile" _hover={{ color: "#162237" }}>
              My Account
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink fontWeight="bold" color="#162237">
              My Orders
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Page Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={4}
          mb={6}
          bg="white"
          p={{ base: 4, md: 6 }}
          rounded="xl"
          shadow="xs"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box>
            <HStack spacing={3}>
              <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="extrabold" color="#162237">
                Order History
              </Text>
              <Badge bg="#162237" color="#EAB308" px={2.5} py={0.5} rounded="full" fontSize="xs">
                {orders.length} {orders.length === 1 ? "Order" : "Orders"}
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Review your previous legal publications, acts, advocate apparel and software purchases.
            </Text>
          </Box>

          {/* Search Input */}
          {orders.length > 0 && (
            <Box w={{ base: "100%", md: "340px" }}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  type="text"
                  placeholder="Search order ID or book title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  rounded="lg"
                  bg="gray.50"
                  borderColor="gray.200"
                  _focus={{ bg: "white", borderColor: "#162237" }}
                />
                {searchTerm && (
                  <InputRightElement>
                    <CloseButton size="xs" onClick={() => setSearchTerm("")} />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          )}
        </Flex>

        {/* Loading Spinner */}
        {loading ? (
          <Center py={20} bg="white" rounded="xl" shadow="xs" border="1px solid" borderColor="gray.200">
            <VStack spacing={4}>
              <Spinner size="xl" color="#162237" thickness="3px" speed="0.7s" />
              <Text color="gray.600" fontSize="sm" fontWeight="medium">
                Fetching your order history...
              </Text>
            </VStack>
          </Center>
        ) : filteredOrders.length === 0 ? (
          /* Empty State */
          <Box
            bg="white"
            rounded="xl"
            shadow="xs"
            border="1px solid"
            borderColor="gray.200"
            p={{ base: 8, md: 16 }}
            textAlign="center"
          >
            <Center mb={4}>
              <Flex
                w="70px"
                h="70px"
                rounded="full"
                bg="blue.50"
                color="#162237"
                align="center"
                justify="center"
                fontSize="32px"
              >
                <FiPackage />
              </Flex>
            </Center>
            <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={1}>
              {searchTerm ? "No matching orders found" : "No orders placed yet"}
            </Text>
            <Text fontSize="xs" color="gray.500" maxW="420px" mx="auto" mb={6}>
              {searchTerm
                ? "Try searching with a different order number or keyword from the title."
                : "Explore our collection of authentic law commentaries, bare acts, and software tools to place your first order."}
            </Text>
            <Link to="/products?maincat=Books">
              <Button
                bg="#162237"
                color="white"
                _hover={{ bg: "#0F172A" }}
                size="sm"
                px={6}
                rounded="lg"
                leftIcon={<FiShoppingBag />}
              >
                Explore Legal Library
              </Button>
            </Link>
          </Box>
        ) : (
          /* Orders List */
          <VStack spacing={5} align="stretch">
            {filteredOrders.map((order, idx) => {
              const statusText = order.status || "Confirmed";
              const isDelivered = statusText.toLowerCase() === "delivered";

              return (
                <Box
                  key={order.orderId || idx}
                  bg="white"
                  rounded="xl"
                  shadow="xs"
                  border="1px solid"
                  borderColor="gray.200"
                  overflow="hidden"
                  transition="all 0.2s"
                  _hover={{ borderColor: "gray.300", shadow: "sm" }}
                >
                  {/* Order Card Header */}
                  <Flex
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align={{ base: "flex-start", sm: "center" }}
                    p={{ base: 4, md: 5 }}
                    bg="#F8FAFC"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    gap={3}
                  >
                    <Flex wrap="wrap" gap={{ base: 4, md: 8 }} align="center">
                      <Box>
                        <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wider">
                          Order Placed
                        </Text>
                        <Text fontSize="xs" color="gray.800" fontWeight="semibold" mt={0.5}>
                          {order.date || "Recent"}
                        </Text>
                      </Box>

                      <Box>
                        <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wider">
                          Total Amount
                        </Text>
                        <Text fontSize="sm" color="#162237" fontWeight="extrabold" mt={0.5}>
                          ₹{Number(order.totalAmount).toFixed(2)}
                        </Text>
                      </Box>

                      <Box>
                        <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wider">
                          Order Reference
                        </Text>
                        <Text fontSize="xs" color="blue.600" fontWeight="bold" mt={0.5}>
                          #{order.orderId}
                        </Text>
                      </Box>
                    </Flex>

                    <HStack spacing={2} alignSelf={{ base: "flex-start", sm: "auto" }}>
                      <Badge
                        colorScheme={isDelivered ? "green" : "blue"}
                        px={2.5}
                        py={1}
                        rounded="md"
                        fontSize="xs"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Icon as={FiCheck} />
                        {statusText}
                      </Badge>

                      <Link to={`/invoice?orderId=${order.orderId}`}>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="navy"
                          borderColor="gray.300"
                          leftIcon={<FiFileText />}
                          rounded="md"
                        >
                          Invoice
                        </Button>
                      </Link>
                    </HStack>
                  </Flex>

                  {/* Items in this Order */}
                  <Box p={{ base: 4, md: 5 }}>
                    {order.items &&
                      order.items.map((item, itemIdx) => (
                        <Box key={item.id || itemIdx}>
                          {itemIdx > 0 && <Divider my={4} borderColor="gray.100" />}
                          <Flex
                            direction={{ base: "column", sm: "row" }}
                            justify="space-between"
                            align={{ base: "flex-start", sm: "center" }}
                            gap={4}
                          >
                            <Flex gap={4} align="center" flex="1">
                              <Box
                                w={{ base: "65px", md: "75px" }}
                                h={{ base: "80px", md: "90px" }}
                                p={2}
                                rounded="lg"
                                border="1px solid"
                                borderColor="gray.200"
                                bg="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                <Image
                                  src={item.imagePath || "https://navneetlawstudio.com/logo.png"}
                                  alt={item.booktitle}
                                  maxH="100%"
                                  maxW="100%"
                                  objectFit="contain"
                                  fallbackSrc="https://navneetlawstudio.com/logo.png"
                                />
                              </Box>

                              <Box flex="1">
                                <Link to={item.id ? `/products/view/${item.id}` : "#"}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color="gray.800"
                                    _hover={{ color: "blue.600" }}
                                    noOfLines={2}
                                    lineHeight="snug"
                                  >
                                    {item.booktitle}
                                  </Text>
                                </Link>

                                <HStack spacing={2} mt={1.5} wrap="wrap">
                                  {item.maincatname && (
                                    <Badge colorScheme="purple" fontSize="10px" rounded="sm">
                                      {item.maincatname}
                                    </Badge>
                                  )}
                                  {item.subcatname && (
                                    <Badge colorScheme="gray" fontSize="10px" rounded="sm">
                                      {item.subcatname}
                                    </Badge>
                                  )}
                                </HStack>

                                <Text fontSize="xs" color="gray.600" mt={1.5}>
                                  Qty: <strong>{item.quantity || 1}</strong> × ₹{Number(item.descprice || 0).toFixed(2)}
                                  {item.gst > 0 && ` (+ GST: ₹${Number(item.gst).toFixed(2)})`}
                                </Text>
                              </Box>
                            </Flex>

                            <HStack spacing={2} alignSelf={{ base: "flex-end", sm: "center" }}>
                              {item.id && (
                                <Link to={`/products/view/${item.id}`}>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="blue"
                                    rounded="md"
                                  >
                                    Buy Again
                                  </Button>
                                </Link>
                              )}
                            </HStack>
                          </Flex>
                        </Box>
                      ))}
                  </Box>
                </Box>
              );
            })}
          </VStack>
        )}
      </Container>
    </Box>
  );
}

export default OrderPage;
